import { BASE, getCsrfToken } from "./constants";

export interface TeamUser {
  nextgis_guid: string;
  username: string;
  dt_joined?: string | null;
}

export interface TeamMember extends TeamUser {
  email?: string;
}

export interface OwnTeam {
  owner: string;
  owner_id?: string;
  users: TeamMember[];
  maximum_size: number;
  team_status: "enabled" | "disabled";
}

export interface TeamMembership {
  owner_id: string;
  users: TeamMember[];
  max_size: number;
  team_status: "enabled" | "disabled";
  webgis: string | null;
  webgis_state: string | null;
  owned_by_target_user: boolean;
}

export interface MemberTeam extends TeamMembership {
  owner_username?: string;
  owner_email?: string;
}

export const DISABLED_WEBGIS_STATES = ["disabled", "wait_for_disable"];
export const NONEXISTANT_WEBGIS_STATES = [
  "error",
  "stopped",
  "wait_for_create",
  "wait_for_start",
];

export const getOwnTeam = async (): Promise<OwnTeam> => {
  const res = await fetch(`${BASE}/team/`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

export const updateTeamUsers = async (
  users: { nextgis_guid: string }[],
): Promise<OwnTeam> => {
  const res = await fetch(`${BASE}/team/`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({ users }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

export const searchUsers = async (username: string): Promise<TeamUser[]> => {
  const res = await fetch(
    `${BASE}/users/?username=${encodeURIComponent(username)}`,
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

export class TeamInvitationError extends Error {
  public readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = "TeamInvitationError";
  }
}

export const inviteTeamUserByEmail = async (email: string): Promise<void> => {
  const res = await fetch(`${BASE}/teams/requests`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new TeamInvitationError(
      body?.code ?? "unknown_error",
      body?.description ?? `HTTP ${res.status}`,
    );
  }
};

export type TeamRequestCategory = "in" | "out";

export interface TeamRequest {
  guid: string;
  date_created: string;
  email: string;
  username?: string | null;
  webgis?: string | null;
}

export interface OutgoingTeamRequest extends TeamRequest {
  invitee_status: "account_exists" | "account_absent";
  invitee_username: string | null;
}

interface TeamRequestsResponse<T extends TeamRequest> {
  status: "ok";
  result: Record<string, Omit<T, "guid">>;
}

export function getTeamRequests(
  category: "out",
): Promise<OutgoingTeamRequest[]>;
export function getTeamRequests(category: "in"): Promise<TeamRequest[]>;
export async function getTeamRequests(
  category: TeamRequestCategory,
): Promise<TeamRequest[]> {
  const res = await fetch(`${BASE}/teams/requests/${category}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data: TeamRequestsResponse<TeamRequest> = await res.json();
  return Object.entries(data.result)
    .map(([guid, request]) => ({
      guid,
      ...request,
    }))
    .sort(
      (left, right) =>
        Date.parse(right.date_created) - Date.parse(left.date_created),
    );
}

type TeamRequestReaction = "accept" | "decline";

const reactToTeamRequest = async (
  guid: string,
  status: TeamRequestReaction,
): Promise<void> => {
  const res = await fetch(`${BASE}/teams/requests/reaction/`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({ guid, status }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

export const cancelTeamInvitation = (guid: string): Promise<void> =>
  reactToTeamRequest(guid, "decline");

export const acceptTeamInvitation = (guid: string): Promise<void> =>
  reactToTeamRequest(guid, "accept");

export const getMemberTeams = async (): Promise<MemberTeam[]> => {
  const res = await fetch(`${BASE}/teams/`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const teams: TeamMembership[] = await res.json();
  return teams
    .filter((t) => !t.owned_by_target_user && t.team_status === "enabled")
    .map((t) => {
      const owner = t.users.find((u) => u.nextgis_guid === t.owner_id);
      return {
        ...t,
        owner_username: owner?.username,
        owner_email: owner?.email,
      };
    });
};

export const leaveTeam = async (ownerGuid: string): Promise<void> => {
  const res = await fetch(
    `${BASE}/teams/${encodeURIComponent(ownerGuid)}/members/me`,
    {
      method: "DELETE",
      headers: { "X-CSRFToken": getCsrfToken() },
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};
